import React from "react";
import { motion } from "framer-motion";
import { Check, X, Info } from "lucide-react";
import { ScrollArea } from "../../ui/scroll-area";

const sections = [
  {
    label: "Billing & Verification",
    features: [
      { name: "Insurance Verification", them: true },
      { name: "Insurance Claim Submission", them: true },
      { name: "Patient Billing & Statements", them: false },
    ],
  },
  {
    label: "Rest of Revenue Cycle",
    features: [
      { name: "Denial Management", them: false },
      { name: "EOB Posting & Reconciliation", them: false },
      { name: "AR & Collections Follow-Up", them: false },
      { name: "Insurance Calling", them: false },
      { name: "Payment Reconciliation", them: false },
    ],
  },
  {
    label: "AI Products",
    features: [
      { name: "AI Receptionist", them: false },
      { name: "AI Hygiene Recall", them: false },
      { name: "AI Case Acceptance", them: false },
      { name: "AI Insurance Verification", them: false },
      { name: "AI Claim Scrubbing", them: false },
      { name: "AI Denial Management", them: false },
      { name: "AI Patient Billing", them: false },
      { name: "AI AR & Collections", them: false },
      { name: "AI EOB Posting", them: false },
      { name: "AI Insurance Calls", them: false },
      { name: "AI Schedule Optimizer", them: false },
      { name: "AI Patient Intake", them: false },
      { name: "AI Practice Manager", them: false },
      { name: "AI Patient Experience", them: false },
      { name: "AI Fax Manager", them: false },
      { name: "AI Copilot", them: false },
      { name: "AI Scribe", them: false },
      { name: "AI Treatment Planner", them: false },
      { name: "AI Revenue Analytics", them: false },
      { name: "AI Claim Submission", them: false },
    ],
  },
  {
    label: "PMS & Clinical",
    features: [
      { name: "Scheduling & Appointments", them: false },
      { name: "Tooth Charting", them: false },
      { name: "Perio Charting", them: false },
      { name: "Treatment Planning", them: false },
      { name: "Clinical Notes", them: false },
      { name: "Patient Ledgers", them: false },
      { name: "Patient Check-In / Checkout", them: false },
      { name: "Medical History", them: false },
      { name: "Screenings", them: false },
      { name: "Audit Trails", them: false },
      { name: "Document Management", them: false },
      { name: "Digital Imaging (SOTA)", them: false },
    ],
  },
  {
    label: "Analytics",
    features: [
      { name: "Revenue Analytics Suite", them: false },
      { name: "Daily Huddles Dashboard", them: false },
      { name: "Collections Reporting", them: false },
      { name: "Production Tracking", them: false },
    ],
  },
  {
    label: "Communication",
    features: [
      { name: "VoIP Phone System", them: false },
      { name: "2-Way Patient Messaging", them: false },
      { name: "Next-Gen Forms", them: false },
      { name: "Intelligent Fax", them: false },
      { name: "SMS Campaigns", them: false },
      { name: "Email Campaigns", them: false },
      { name: "Physical Phones", them: false },
      { name: "Online Scheduling", them: false },
      { name: "Team Chat", them: false },
    ],
  },
  {
    label: "Payments & Platform",
    features: [
      { name: "Text-to-Pay", them: false },
      { name: "Terminal Card Readers", them: false },
      { name: "eClaims Portal", them: false },
      { name: "Patient Portal", them: false },
      { name: "Marketing & Campaigns", them: false },
      { name: "ePrescribing (Coming Soon)", them: false },
    ],
  },
];

const totalTheirs = sections.reduce((sum, s) => sum + s.features.filter(f => f.them).length, 0);
const totalFeatures = sections.reduce((sum, s) => sum + s.features.length, 0);

const PricingComparisonV3Slide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-stretch p-5 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }} />
      <div className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }} />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex gap-10 relative z-10 h-full max-w-[1240px] mx-auto items-center">
        {/* Left side — title */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-[420px] flex-shrink-0 flex flex-col justify-center"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-100/60 border border-blue-200 text-blue-800 text-sm font-semibold mb-6 shadow-sm w-fit">
            <Info className="w-4 h-4" /> Comprehensive Comparison
          </div>
          <h2 className="text-[44px] lg:text-[50px] font-extrabold text-slate-900 tracking-tight leading-[1.1] mb-8">
            How does this compare to other vendors{" "}
            <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">based on their published rates?</span>
          </h2>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center shadow-sm">
                  <X className="w-4 h-4 text-slate-500" />
                </div>
              </div>
              <p className="text-[17px] text-slate-600 leading-relaxed font-medium">
                eAssist and Wisdom charge more for <span className="text-slate-900 font-bold">just insurance verification and billing.</span> 
              </p>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0 mt-1">
                <div className="w-8 h-8 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center shadow-sm shadow-blue-500/20">
                  <Check className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              <p className="text-[17px] text-slate-600 leading-relaxed font-medium">
                Asha gives you an entire AI workforce and operating system for <span className="text-blue-600 font-bold">significantly less.</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Right side — comparison table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="flex-1 rounded-[24px] overflow-hidden shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)] border border-slate-200 bg-white flex flex-col h-[640px] relative"
        >
          {/* Column headers (Sticky) */}
          <div className="grid grid-cols-[1fr_180px_180px] border-b border-slate-200 flex-shrink-0 bg-white z-20 relative shadow-sm">
            <div className="px-6 py-5 flex items-end">
              <span className="text-xs font-black uppercase tracking-widest text-slate-400">Features Included</span>
            </div>
            <div className="px-4 py-5 text-center border-l border-slate-100 bg-gradient-to-b from-slate-50 to-white relative flex flex-col justify-end">
              <p className="text-xs font-black uppercase tracking-wider text-slate-500 mb-1">eAssist / Wisdom</p>
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="text-2xl font-extrabold text-slate-800">~$2,400</span>
                <span className="text-xs font-bold text-slate-400">/mo</span>
              </div>
              <span className="text-[13px] font-bold text-slate-500 mt-1 block">+ 3.5% of collections</span>
            </div>
            <div className="px-4 py-5 text-center border-l border-blue-100 bg-gradient-to-b from-blue-50/80 to-white relative flex flex-col justify-end items-center">
              {/* Highlight badge */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-sm uppercase tracking-widest mb-2 w-fit">
                Asha OS
              </div>
              <p className="text-xs font-black uppercase tracking-wider text-blue-600 mb-1">Asha</p>
              <div className="flex items-baseline justify-center gap-0.5">
                <span className="text-2xl font-extrabold text-slate-900">$799</span>
                <span className="text-xs font-bold text-slate-400">/mo</span>
              </div>
              <span className="text-[13px] font-bold text-blue-600 mt-1 block">+ 3.5% of collections</span>
            </div>
          </div>

          {/* Scrollable feature rows */}
          <ScrollArea className="flex-1 bg-white">
            <div className="pb-6">
              {sections.map((section, si) => (
                <div key={si} className="mb-0">
                  <div className="grid grid-cols-[1fr_180px_180px] bg-slate-50/90 border-y border-slate-100 sticky top-0 z-10 backdrop-blur-xl">
                    <div className="px-6 py-2.5 flex items-center gap-2">
                      <div className="w-1 h-3.5 rounded-full bg-blue-400/50"></div>
                      <span className="text-[11px] font-bold uppercase tracking-widest text-slate-600">{section.label}</span>
                    </div>
                    <div className="border-l border-slate-100" />
                    <div className="border-l border-blue-50/50 bg-blue-50/20" />
                  </div>
                  {section.features.map((f, fi) => (
                    <div key={fi} className="grid grid-cols-[1fr_180px_180px] border-b border-slate-50/50 hover:bg-slate-50/50 transition-colors group">
                      <div className="px-6 py-3.5 text-[14px] text-slate-700 font-medium flex items-center group-hover:text-slate-900 transition-colors">
                        {f.name}
                      </div>
                      <div className="py-3.5 flex items-center justify-center border-l border-slate-100/50">
                        {f.them ? (
                          <div className="w-[26px] h-[26px] rounded-full bg-slate-100 flex items-center justify-center border border-slate-200">
                            <Check className="w-4 h-4 text-slate-600" />
                          </div>
                        ) : (
                          <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center opacity-30">
                            <X className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </div>
                      <div className="py-3.5 flex items-center justify-center border-l border-blue-50/50 bg-blue-50/20 group-hover:bg-blue-50/40 transition-colors">
                        <div className="w-[26px] h-[26px] rounded-full bg-blue-100 flex items-center justify-center border border-blue-200 shadow-[0_2px_8px_rgba(59,130,246,0.15)]">
                          <Check className="w-4 h-4 text-blue-600" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </ScrollArea>

          {/* Summary bar */}
          <div className="grid grid-cols-[1fr_180px_180px] border-t border-slate-200 bg-white flex-shrink-0 z-20 relative shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
            <div className="px-6 py-5 flex items-center">
              <span className="text-xs font-black text-slate-400 uppercase tracking-wider">What you get</span>
            </div>
            <div className="py-5 text-center border-l border-slate-100 bg-slate-50">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-[19px] font-black text-slate-700">{totalTheirs}</span>
                <span className="text-xs font-bold text-slate-400">/ {totalFeatures}</span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mt-0.5">Capabilities</span>
            </div>
            <div className="py-5 text-center border-l border-blue-100 bg-blue-50">
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-[19px] font-black text-blue-700">{totalFeatures}</span>
                <span className="text-xs font-bold text-blue-400">/ {totalFeatures}</span>
              </div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 block mt-0.5">Capabilities</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingComparisonV3Slide;
